.button {
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 18px;
  border-radius: var(--r-ctl);
  border: 0;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.003em;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    transform var(--t-fast) var(--ease),
    background-color var(--t-fast) var(--ease),
    background-position var(--t-slow) var(--ease),
    box-shadow var(--t) var(--ease),
    opacity var(--t) var(--ease);
}
.button:active:not(:disabled) {
  transform: scale(0.97);
}
.button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.sm {
  height: 32px;
  padding: 0 14px;
  font-size: 12.5px;
}
.lg {
  height: 48px;
  padding: 0 22px;
  font-size: 15px;
}
.primary {
  background: var(--pri);
  color: var(--onpri);
}
.approve {
  background: var(--hum);
  color: var(--onhum);
}
.field {
  background: var(--field);
  color: var(--ink);
}
.ghost {
  background: transparent;
  color: var(--ink3);
}
.ghost:hover:not(:disabled) {
  background: var(--hov);
}
.danger {
  background: var(--crits2);
  color: var(--critt);
}
.spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.tone-neutral {
  background: var(--chip);
  color: var(--ink3);
}
.tone-card {
  background: var(--card);
  color: var(--ink3);
}
.tone-ok {
  background: var(--oks);
  color: var(--okt);
}
.tone-hum {
  background: var(--hums3);
  color: var(--humt);
}
.tone-acc {
  background: var(--accs);
  color: var(--acct);
}
.tone-crit {
  background: var(--crits2);
  color: var(--critt);
}
.tone-solid-hum {
  background: var(--hum);
  color: var(--onhum);
}

.segmented {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: var(--r-ctl);
  background: var(--field);
}
.segmented button {
  height: 32px;
  padding: 0 14px;
  border-radius: 7px;
  border: 0;
  background: transparent;
  color: var(--mut);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.segmented button[aria-pressed="true"] {
  background: var(--card);
  box-shadow: var(--sh1);
  color: var(--ink);
  font-weight: 600;
}

.switch {
  width: 40px;
  height: 24px;
  flex: 0 0 40px;
  border-radius: 12px;
  border: 0;
  padding: 3px;
  display: flex;
  cursor: pointer;
  background: var(--ln3);
  transition: background-color 0.2s ease;
}
.switch[aria-checked="true"] {
  background: var(--ok);
  justify-content: flex-end;
}
.switch:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.knob {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
  color: var(--mut);
}
.stateTitle {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
}
.stateText {
  max-width: 52ch;
  font-size: 14px;
  line-height: 1.5;
  text-wrap: pretty;
}
.stateError {
  gap: 8px;
}
.stateError > button {
  margin-top: 6px;
}
.stateMark {
  width: 36px;
  height: 36px;
  margin-bottom: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--crits);
  color: var(--critt);
  box-shadow: inset 0 0 0 1px var(--critl);
}
.stateDetail {
  font-family: var(--mono);
  font-size: 12px;
  color: var(--fnt);
}
.skeleton {
  max-width: 100%;
  border-radius: 14px;
  background: linear-gradient(90deg, var(--field) 0%, var(--raised) 50%, var(--field) 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}
@keyframes shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .skeleton,
  .spinner {
    animation: none;
  }
  .button {
    transition: none;
  }
}

/* Hold to approve: the fill crosses the button while it is held; letting go early cancels. */
.hold {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}
.holdLabel {
  display: inline-grid;
}
.holdLabel > span {
  grid-area: 1 / 1;
  text-align: center;
}
.holdLabel > [aria-hidden="true"] {
  visibility: hidden;
}
.holdFill {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: color-mix(in srgb, var(--onhum) 26%, transparent);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 260ms var(--ease);
}
.holding .holdFill {
  transform: scaleX(1);
  transition: transform var(--hold-ms, 1200ms) linear;
}
.holdRing {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}
.holdRing circle {
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
}
.holdRingTrack {
  opacity: 0.3;
}
.holdRingFill {
  stroke-dasharray: 44;
  stroke-dashoffset: 44;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  transition: stroke-dashoffset 260ms var(--ease);
}
.holding .holdRingFill {
  stroke-dashoffset: 0;
  transition: stroke-dashoffset var(--hold-ms, 1200ms) linear;
}
.held {
  animation: held 520ms var(--ease);
}
@keyframes held {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.04);
  }
  100% {
    transform: scale(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .held {
    animation: none;
  }
}
